import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, Plus, ArrowRight, Gavel, FileText, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import LegalDisclaimerModal from "@/components/LegalDisclaimerModal";

/**
 * Sprint K · Legal Analysis List (/app/legal/analysis)
 *
 * KPI strip → tabs (My analyses / Templates) → grid.
 * Non-owner without an attorney declaration is bounced to
 * /app/legal/analysis/attorney-declaration.
 */

const KPI_TILE_STYLE = {
    background: "var(--atlas-bg-elevated)",
    border: "1px solid var(--atlas-border-soft)",
};

export default function LegalAnalysisList() {
    const nav = useNavigate();
    const { user, company } = useAuth();
    const isOwner = user?.role === "platform_owner";

    const [ready, setReady] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [orchs, setOrchs] = useState([]);
    const [tab, setTab] = useState("mine");
    const [kpis, setKpis] = useState({ this_month: 0, awaiting: 0, total: 0, areas: 0 });

    useEffect(() => {
        if (isOwner) return;
        // Non-owner: require an active declaration before landing on this page.
        api.get("/legal/attorney-declaration/me").catch((e) => {
            if (e?.response?.status === 404) {
                nav("/app/legal/analysis/attorney-declaration", { replace: true });
            }
        });
    }, [isOwner, nav]);

    useEffect(() => {
        (async () => {
            try {
                const [tpl, orch, audit] = await Promise.all([
                    api.get("/orchestrations/templates"),
                    api.get("/orchestrations"),
                    api.get("/legal/audit-log?limit=200").catch(() => ({ data: { audit_log: [] } })),
                ]);
                const allT = tpl.data.templates || [];
                setTemplates(allT.filter((t) => t.is_legal));
                const allO = orch.data.orchestrations || [];
                setOrchs(allO.filter((o) => o.is_legal || (o.template_slug || "").startsWith("legal-")));

                const rows = audit.data.audit_log || [];
                const monthStart = new Date();
                monthStart.setDate(1);
                monthStart.setHours(0, 0, 0, 0);
                const thisMonth = rows.filter(
                    (r) => new Date(r.started_at) >= monthStart
                ).length;
                const awaiting = rows.filter((r) => r.status === "awaiting_approval").length;
                const areas = new Set(rows.map((r) => r.practice_area).filter(Boolean)).size;
                setKpis({ this_month: thisMonth, awaiting, total: rows.length, areas });
            } catch (err) {
                console.warn("Legal analysis load failed:", err?.message);
            }
        })();
    }, []);

    return (
        <>
            <LegalDisclaimerModal
                tenantId={company?.id || user?.id || "unknown"}
                onAcknowledge={() => setReady(true)}
            />
            {(isOwner || ready) && (
                <div className="h-full overflow-y-auto wfos-scrollbar" data-testid="legal-analysis-page">
                    <div className="max-w-6xl mx-auto p-8">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <div className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    Legal · Analysis Studio
                                </div>
                                <h1
                                    className="text-3xl font-semibold mt-1"
                                    style={{ color: "var(--atlas-text-primary)" }}
                                >
                                    Legal Analysis
                                </h1>
                                <p
                                    className="mt-2 text-sm max-w-2xl"
                                    style={{ color: "var(--atlas-text-secondary)" }}
                                >
                                    Multi-step legal analyses executed by your Workforce Members
                                    with attorney-of-record acknowledgment. AI-generated legal
                                    information only — every output requires attorney review.
                                </p>
                            </div>
                        </div>

                        {/* KPI strip */}
                        <div className="grid grid-cols-4 gap-3 mt-6">
                            {[
                                { label: "Analyses this month",         value: kpis.this_month },
                                { label: "Awaiting attorney review",    value: kpis.awaiting },
                                { label: "Total analyses",              value: kpis.total },
                                { label: "Practice areas active",       value: kpis.areas },
                            ].map((k, i) => (
                                <div key={i} className="rounded-xl p-4" style={KPI_TILE_STYLE}>
                                    <div
                                        className="wfos-label"
                                        style={{ color: "var(--atlas-text-tertiary)" }}
                                    >
                                        {k.label}
                                    </div>
                                    <div
                                        className="text-2xl font-semibold mt-1"
                                        style={{ color: "var(--atlas-text-primary)" }}
                                    >
                                        {k.value}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Tabs */}
                        <div
                            className="mt-8 flex items-center gap-1 border-b"
                            style={{ borderColor: "var(--atlas-border-soft)" }}
                        >
                            {[
                                { key: "mine", label: "My analyses" },
                                { key: "templates", label: "Templates" },
                            ].map((t) => {
                                const on = tab === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        onClick={() => setTab(t.key)}
                                        className="px-4 py-2 text-sm relative"
                                        style={{
                                            color: on ? "var(--atlas-text-primary)" : "var(--atlas-text-tertiary)",
                                        }}
                                        data-testid={`legal-analysis-tab-${t.key}`}
                                    >
                                        {t.label}
                                        {on && (
                                            <span
                                                className="absolute -bottom-px left-0 right-0 h-0.5"
                                                style={{ background: "var(--atlas-primary)" }}
                                            />
                                        )}
                                    </button>
                                );
                            })}
                            <div className="ml-auto pb-2">
                                <Link
                                    to="/app/legal/analysis/new"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
                                    style={{
                                        background: "var(--atlas-primary)",
                                        color: "white",
                                    }}
                                    data-testid="legal-analysis-new"
                                >
                                    <Plus className="w-4 h-4" strokeWidth={2} />
                                    New analysis
                                </Link>
                            </div>
                        </div>

                        {/* Grid */}
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {tab === "templates" &&
                                templates.map((t) => (
                                    <TemplateCard key={t.slug} tpl={t} />
                                ))}
                            {tab === "mine" && orchs.length === 0 && (
                                <div
                                    className="col-span-full text-center p-10 rounded-xl"
                                    style={{
                                        background: "var(--atlas-bg-elevated)",
                                        border: "1px dashed var(--atlas-border-soft)",
                                        color: "var(--atlas-text-tertiary)",
                                    }}
                                >
                                    You haven't started a legal analysis yet.{" "}
                                    <Link
                                        to="/app/legal/analysis/new"
                                        className="underline"
                                        style={{ color: "var(--atlas-primary-hover)" }}
                                    >
                                        Choose a template →
                                    </Link>
                                </div>
                            )}
                            {tab === "mine" &&
                                orchs.map((o) => <AnalysisCard key={o.id} orch={o} />)}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function TemplateCard({ tpl }) {
    return (
        <Link
            to={`/app/legal/analysis/new?template=${tpl.slug}`}
            className="block rounded-xl p-5 transition hover:brightness-110"
            style={{
                background: "var(--atlas-bg-elevated)",
                border: "1px solid var(--atlas-border-soft)",
            }}
            data-testid={`legal-template-${tpl.slug}`}
        >
            <div className="flex items-start gap-3">
                <div
                    className="w-10 h-10 rounded-lg grid place-items-center flex-shrink-0 text-lg"
                    style={{
                        background: "rgba(139,92,246,0.14)",
                        border: "1px solid rgba(139,92,246,0.35)",
                    }}
                >
                    {tpl.icon || "⚖️"}
                </div>
                <div className="min-w-0 flex-1">
                    <div
                        className="text-sm font-semibold"
                        style={{ color: "var(--atlas-text-primary)" }}
                    >
                        {tpl.name}
                    </div>
                    <div
                        className="mt-1 text-xs"
                        style={{ color: "var(--atlas-text-secondary)" }}
                    >
                        {tpl.description}
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap text-[10px]">
                        <span
                            className="px-2 py-0.5 rounded-full"
                            style={{
                                background: "rgba(52,211,153,0.14)",
                                border: "1px solid rgba(52,211,153,0.35)",
                                color: "#34d399",
                            }}
                        >
                            {tpl.node_count} steps
                        </span>
                        {tpl.requires_attorney_declaration && (
                            <span
                                className="px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                                style={{
                                    background: "rgba(245,158,11,0.14)",
                                    border: "1px solid rgba(245,158,11,0.35)",
                                    color: "#fbbf24",
                                }}
                            >
                                <ShieldCheck className="w-3 h-3" strokeWidth={2} />
                                Attorney gate
                            </span>
                        )}
                    </div>
                </div>
                <ArrowRight
                    className="w-4 h-4 mt-1 flex-shrink-0"
                    style={{ color: "var(--atlas-text-tertiary)" }}
                    strokeWidth={1.75}
                />
            </div>
        </Link>
    );
}

function AnalysisCard({ orch }) {
    return (
        <Link
            to={`/app/legal/analysis/${orch.id}`}
            className="block rounded-xl p-5 transition hover:brightness-110"
            style={{
                background: "var(--atlas-bg-elevated)",
                border: "1px solid var(--atlas-border-soft)",
            }}
            data-testid={`legal-analysis-${orch.id}`}
        >
            <div className="flex items-start gap-3">
                <Gavel
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: "var(--atlas-primary)" }}
                    strokeWidth={1.75}
                />
                <div className="min-w-0 flex-1">
                    <div
                        className="text-sm font-semibold"
                        style={{ color: "var(--atlas-text-primary)" }}
                    >
                        {orch.name}
                    </div>
                    <div
                        className="mt-1 text-xs"
                        style={{ color: "var(--atlas-text-secondary)" }}
                    >
                        {orch.description}
                    </div>
                    <div className="mt-2 text-[10px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                        Status: {orch.status}
                    </div>
                </div>
            </div>
        </Link>
    );
}
